import { Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { lazy, Suspense, useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';

import { LanguageProvider } from './contexts/LanguageContext';
import Header from './components/layout/Header';
import ModernFooter from './components/layout/ModernFooter';
import ErrorBoundary from './components/ErrorBoundary';
import SEOHead from './components/seo/SEOHead';
import Analytics from './components/analytics/Analytics';
import SitemapGenerator, { SitemapURL } from './components/seo/SitemapGenerator';
import ScrollToTop from './components/common/ScrollToTop';
import RedirectHandler from './components/common/RedirectHandler';
import { useDataRetention } from "@/hooks/useDataRetention";

// Route-based code splitting with lazy loading
const Index = lazy(() => import('./pages/Index'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const About = lazy(() => import('./pages/About'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const BiodataProfileMaker = lazy(() => import('./components/tools/biodataProfileMaker/BiodataProfileMaker'));
const DowryCalculator = lazy(() => import('./pages/DowryCalculator'));
const PartnerCompatibility = lazy(() => import('./pages/PartnerCompatibility'));
const Disclaimer = lazy(() => import('./pages/Disclaimer'));
const NotFound = lazy(() => import('./pages/NotFound'));
const AuthorPage = lazy(() => import('./pages/AuthorPage'));
const DMCA = lazy(() => import('./pages/DMCA'));
const WeddingHashtagGenerator = lazy(() => import('./pages/WeddingHashtagGenerator'));

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

// Configure React Query for performance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

import { SUPPORTED_LANGUAGES } from './config/languages';

// Define inner routes component
const AppRoutes = () => (
  <Routes>
    <Route index element={<Index />} />
    <Route path="create" element={<Index />} />
    <Route path="blog" element={<Blog />} />
    <Route path="blog/:slug" element={<BlogPost />} />
    <Route path="contact-us" element={<ContactUs />} />
    <Route path="about" element={<About />} />
    <Route path="terms" element={<TermsOfService />} />
    <Route path="privacy" element={<PrivacyPolicy />} />
    <Route path="biodata-profile-maker" element={<BiodataProfileMaker />} />
    <Route path="dowry-calculator" element={<DowryCalculator />} />
    <Route path="partner-compatibility" element={<PartnerCompatibility />} />
    <Route path="wedding-hashtag-generator" element={<WeddingHashtagGenerator />} />
    <Route path="disclaimer" element={<Disclaimer />} />
    <Route path="author/:slug" element={<AuthorPage />} />
    <Route path="dmca" element={<DMCA />} />
    {/* Add a 404 route */}
    <Route path="*" element={<NotFound />} />
  </Routes>
);

function App() {
  // Configure scroll behavior once on app initialization
  useEffect(() => {
    // Prevent browser scroll restoration
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, []);

  useDataRetention({
    onPurged: () => {
      queryClient.clear();
    },
  });

  // Minimal sitemap URLs for the SitemapGenerator (main sitemap is in public/sitemap.xml)
  const sitemapUrls: SitemapURL[] = [
    { loc: '/', lastmod: '2025-07-26', changefreq: 'weekly', priority: 1.0 },
    { loc: '/blog', lastmod: '2025-07-26', changefreq: 'weekly', priority: 0.8 }
  ];

  return (
    <HelmetProvider>
      <LanguageProvider>
        <QueryClientProvider client={queryClient}>
          <ErrorBoundary>
            {/* Global SEO components */}
            <SEOHead />
            <Analytics />
            <SitemapGenerator baseUrl="https://biodata360.in" urls={sitemapUrls} />

            <div className="min-h-screen bg-background">
              <ScrollToTop />
              <RedirectHandler />
              <Header />
              <main className="pt-20">
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* Language prefixed routes */}
                    {SUPPORTED_LANGUAGES.map(lang => (
                      <Route key={lang} path={`/${lang}/*`} element={<AppRoutes />} />
                    ))}
                    {/* Default routes */}
                    <Route path="/*" element={<AppRoutes />} />
                  </Routes>
                </Suspense>
              </main>
              <ModernFooter />
              <Toaster />
            </div>
          </ErrorBoundary>
        </QueryClientProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
